import {
  type ElementType,
  forwardRef,
  useEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
} from "react"
import { mergeRefs } from "react-merge-refs"

export type TruncateProps = ComponentPropsWithoutRef<"span"> & {
  /**
   * Indicates how many lines should be shown before the text is truncated.
   *
   * @default 1
   */
  lineCount?: 1 | 2 | 3 | 4 | 5

  /**
   * The innermost element to be rendered by this component.
   * In other words, the element that will contain the text to be truncated.
   *
   * @default 'span'
   */
  containerElement?: ElementType
}

export const Truncate = forwardRef<HTMLElement, TruncateProps>(
  (
    {
      className,
      lineCount,
      containerElement: ContainerElement = "span",
      ...props
    },
    ref,
  ) => {
    return (
      <ContainerElement
        ref={ref}
        className={`truncate ${className ?? ""}`}
        data-line-count={lineCount}
        {...props}
      />
    )
  },
)

Truncate.displayName = "Truncate"

export const ExpandableTruncate = forwardRef<HTMLElement, TruncateProps>(
  (
    {
      className,
      lineCount,
      containerElement: ContainerElement = "span",
      children,
      ...props
    },
    ref,
  ) => {
    const [expanded, setExpanded] = useState(false)
    const [truncated, setTruncated] = useState(false)

    /* If we detect we don't need to truncate the text at all, set the state accordingly,
     * so we don't show the '...more' button or the mask gradient.
     */
    const localRef = useRef<HTMLElement>(null)
    useEffect(() => {
      if (!expanded && localRef.current) {
        setTruncated(
          localRef.current.scrollHeight !== localRef.current.clientHeight,
        )
      }
    }, [children, lineCount])

    return (
      <div className={`expandable-truncate-container ${className ?? ""}`}>
        {expanded ? (
          <ContainerElement ref={mergeRefs([localRef, ref])} {...props}>
            {children}
          </ContainerElement>
        ) : (
          <Truncate
            ref={mergeRefs([localRef, ref])}
            lineCount={lineCount}
            data-truncated={truncated}
            data-expanded={expanded}
            containerElement={ContainerElement}
            {...props}
          >
            {children}
          </Truncate>
        )}
        {truncated && !expanded && (
          <button
            className="truncate-expand-button text-color-primary"
            type="button"
            aria-label="More"
            onClick={() => {
              setExpanded(true)
              setTruncated(false)
            }}
          >
            ...more
          </button>
        )}
      </div>
    )
  },
)

ExpandableTruncate.displayName = "ExpandableTruncate"
